import React, { useRef } from "react";

import {
  AppBar,
  Toolbar,
  Typography,
  makeStyles,
  Button as MuiButton,
  ButtonGroup,
  Dialog,
  DialogActions,
  DialogContent,
  Box,
  useTheme,
  useMediaQuery,
  ClickAwayListener,
  Grow,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Tooltip,
  ButtonProps,
  withStyles,
} from "@material-ui/core";

//import SaveIcon from "@material-ui/icons/SaveAlt";
import LoadIcon from "@material-ui/icons/SaveAlt";
import DeleteIcon from "@material-ui/icons/Delete";
import PdfIcon from "@material-ui/icons/PictureAsPdf";
import LaserIcon from "@material-ui/icons/FlashOn";
import LabelIcon from "@material-ui/icons/LocalShipping";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import SendIcon from "@material-ui/icons/Send";

//import { saveAs } from "file-saver";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "./store";
import { persistor } from "./store";
import { loadAction } from "./store/actions";
import {
  downloadPdf,
  generatePdf,
  laserExport,
  packingLabelExport,
} from "./export";
import transient from "./store/transient";
import { useAdvanced } from "./hooks";

import { ReactComponent as BasalteWorld } from "../svg/basalte-world.svg";
import { URLRoot } from "..";

const pdfjs = require("pdfjs-dist");
// I give up
pdfjs.GlobalWorkerOptions.workerSrc =
  "https://cdn.jsdelivr.net/npm/pdfjs-dist@2.5.207/build/pdf.worker.js";

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: "#000000",
    color: "#ffffff",
  },
  title: {
    flexGrow: 1,
  },
  input: {
    display: "none",
  },
}));

const Button = withStyles({
  root: {
    "&.Mui-disabled": {
      pointerEvents: "auto",
      border: "1px solid rgba(200,200,200, 0.3)",
      color: "rgba(200,200,200, 0.5)",
    },
  },
})(MuiButton);

const ButtonWithTooltip = ({
  tooltip,
  disabled,
  onClick,
  ...other
}: ButtonProps & { tooltip?: string }) => {
  const adjustedButtonProps = {
    disabled: disabled,
    component: disabled ? "div" : undefined,
    onClick: disabled ? undefined : onClick,
  };
  return (
    <Tooltip title={tooltip ?? ""}>
      <Button {...other} {...adjustedButtonProps} />
    </Tooltip>
  );
};

const Header = () => {
  const [confirmation, setConfirmation] = React.useState<
    [(() => void) | null, string]
  >([null, ""]);
  const uploadRef = useRef<HTMLInputElement>(null);

  const classes = useStyles();
  const dispatch = useDispatch();

  const document = useSelector((state: RootState) => state.document);
  /*const saveDocument = () => {
    saveAs(
      new Blob([JSON.stringify(document)], {
        type: "text/plain;charset=utf-8",
      }),
      `${
        document.metadata.title.length ? document.metadata.title : "Untitled"
      }.fibo`
    );
  };*/

  const setExporting = (exporting: boolean) =>
    dispatch(transient.actions.setExporting(exporting));

  const resetAll = () => {
    setConfirmation([
      () => persistor.purge().then(() => window.location.reload()),
      "Are you sure you want to reset everything?",
    ]);
  };

  const sendToBasalte = () => {
    const toBase64 = (blob: Blob) =>
      new Promise<string>((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = () => {
          const b64 = (reader.result as string).replace(/^data:.+;base64,/, "");
          resolve(b64);
        };
        reader.onerror = reject;

        reader.readAsDataURL(blob);
      });

    const doSend = () => {
      setExporting(true);
      return generatePdf(document)
        .then(async (blob) =>
          fetch(URLRoot + "/sendToBasalte", {
            method: "POST",
            body: JSON.stringify({
              worldOrder: document.metadata.worldOrder,
              pdf: await toBase64(blob),
              product: "Fibonacci",
            }),
          })
        )
        .then(async (res) => {
          if (!res.ok) throw new Error(await res.text());

          alert("Successfully sent engravings to Basalte!");
        })
        .catch((err) =>
          alert("Something went wrong! " + err.message ?? err.toString())
        )
        .finally(() => setExporting(false));
    };

    setConfirmation([
      doSend,
      "Are you sure you want to send the configured engravings to Basalte?",
    ]);
  };

  const loadDocument = () => {
    uploadRef.current?.click();
  };

  const exportToPdf = async () => {
    setExporting(true);
    await downloadPdf(document).catch(alert);
    setExporting(false);
  };

  const exportLaser = async () => {
    setExporting(true);
    await laserExport(document).catch(alert);
    setExporting(false);
  };

  const exportLabel = async () => {
    setExporting(true);
    await packingLabelExport(document).catch(alert);
    setExporting(false);
  };

  const importStateFromPDF = (files: FileList) => {
    const reader = new FileReader();
    if (files.length > 0) {
      reader.readAsArrayBuffer(files[0]);

      reader.onload = async () => {
        try {
          if (reader.result) {
            const doc = await pdfjs.getDocument(reader.result as string)
              .promise;
            const pdfMetadata = await doc.getMetadata();
            const json = (pdfMetadata.info.Custom || {}).json;
            if (json) {
              const document = JSON.parse(json);
              dispatch(loadAction(document as RootState["document"]));
            } else {
              throw new Error("No engraver metadata found!");
            }
          } else {
            throw new Error("Could not load file!");
          }
        } catch (err) {
          alert(`Could not import PDF! Error: ${err}`);
        }
      };
    }
  };

  const { advanced } = useAdvanced();
  const theme = useTheme();
  const smallDisplay = useMediaQuery(theme.breakpoints.down("sm"));
  const [buttonMenuOpen, setButtonMenuOpen] = React.useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);

  const validWorldOrder = !!document.metadata.worldOrder.match(
    /^[0-9]{9}[0-9]?$/
  );

  const buttons = [
    advanced
      ? {
          key: "laser",
          onClick: exportLaser,
          endIcon: <LaserIcon />,
          children: "Laser Export",
        }
      : undefined,
    advanced
      ? {
          key: "label",
          onClick: exportLabel,
          endIcon: <LabelIcon />,
          children: "Label Export",
        }
      : undefined,
    {
      key: "sendToBasalte",
      onClick: sendToBasalte,
      endIcon: <SendIcon />,
      children: "Send to Basalte",
      mainButton: validWorldOrder,
      disabled: !validWorldOrder,
      tooltip: validWorldOrder
        ? undefined
        : "A valid Basalte World order number is required!",
    },
    {
      key: "download",
      onClick: exportToPdf,
      endIcon: <PdfIcon />,
      children: "Download",
      mainButton: !validWorldOrder,
    },
    {
      key: "import",
      onClick: loadDocument,
      endIcon: <LoadIcon />,
      children: "Import",
    },
    {
      key: "reset",
      onClick: resetAll,
      endIcon: <DeleteIcon />,
      children: "Reset",
      color: "secondary" as "secondary",
    },
  ];

  return (
    <AppBar position="static" className={classes.root}>
      <input
        className={classes.input}
        accept=".fibo,.pdf"
        id="contained-button-file"
        multiple
        type="file"
        ref={uploadRef}
        onChange={(ev) => {
          const files = ev.target.files;

          if (files) {
            if (files[0] && files[0].name.toLowerCase().endsWith(".fibo")) {
              files[0].text().then((text) => {
                try {
                  const loadedDocument = JSON.parse(text);
                  dispatch(loadAction(loadedDocument as RootState["document"]));
                } catch (err) {
                  alert(`Could not import .fibo! Error: ${err}`);
                }
              });
            } else {
              importStateFromPDF(files);
            }
          }
        }}
      />
      <Toolbar>
        <Box
          display="flex"
          width="100%"
          flexDirection={{ xs: "column", sm: "row" }}
          p={{ xs: 1, sm: 0 }}
        >
          <Typography
            variant="h6"
            className={classes.title}
            component="div"
            gutterBottom={smallDisplay}
          >
            <Box height="2rem" display="flex" alignItems="center">
              <BasalteWorld height="32px" />
              <Box style={{ marginBottom: "-5px" }} ml={2} whiteSpace="nowrap">
                Fibonacci Configurator
              </Box>
            </Box>
          </Typography>
          <ButtonGroup
            color="primary"
            aria-label="outlined primary button group"
          >
            {smallDisplay
              ? [
                  buttons
                    .filter(Boolean)
                    .filter((b) => b?.mainButton)
                    .map((action) => (
                      <Button
                        key={action?.key}
                        disabled={action?.disabled}
                        endIcon={action?.endIcon}
                        onClick={action?.onClick}
                        color={action?.color}
                      >
                        {action?.children}
                      </Button>
                    )),
                  <Button
                    key="more"
                    size="small"
                    onClick={() => setButtonMenuOpen(true)}
                    ref={anchorRef}
                  >
                    <ArrowDropDownIcon />
                  </Button>,
                ]
              : buttons.filter(Boolean).map((action) => (
                  <ButtonWithTooltip
                    size="small"
                    key={action?.key}
                    disabled={action?.disabled}
                    endIcon={action?.endIcon}
                    onClick={action?.onClick}
                    color={action?.color}
                    tooltip={action?.tooltip}
                  >
                    {action?.children}
                  </ButtonWithTooltip>
                ))}
          </ButtonGroup>
          <Popper
            open={buttonMenuOpen}
            anchorEl={anchorRef.current}
            role={undefined}
            transition
            disablePortal
            placement="bottom-end"
            style={{ zIndex: 10 }}
          >
            {({ TransitionProps, placement }) => (
              <Grow
                {...TransitionProps}
                style={{
                  transformOrigin:
                    placement === "bottom" ? "center top" : "center bottom",
                }}
              >
                <Paper>
                  <ClickAwayListener
                    onClickAway={() => setButtonMenuOpen(false)}
                  >
                    <MenuList>
                      {buttons
                        .filter(Boolean)
                        .filter((b) => !b?.mainButton)
                        .map((action) => (
                          <Tooltip
                            key={action?.key ?? ""}
                            title={action?.tooltip ?? ""}
                            arrow
                            placement="top"
                          >
                            <span>
                              <MenuItem
                                onClick={action?.onClick}
                                disabled={action?.disabled}
                              >
                                <Box
                                  width="100%"
                                  justifyContent="end"
                                  color={
                                    action?.color
                                      ? theme.palette[action.color].main
                                      : undefined
                                  }
                                  display="flex"
                                >
                                  <Box mr={1}>
                                    <Typography variant="button">
                                      {action?.children}
                                    </Typography>
                                  </Box>
                                  {action?.endIcon}
                                </Box>
                              </MenuItem>
                            </span>
                          </Tooltip>
                        ))}
                    </MenuList>
                  </ClickAwayListener>
                </Paper>
              </Grow>
            )}
          </Popper>
        </Box>
        <Dialog
          open={!!confirmation[0]}
          onClose={() => {
            setConfirmation([null, ""]);
          }}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogContent id="alert-dialog-title">
            {confirmation[1]}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setConfirmation([null, ""]);
              }}
              color="primary"
            >
              No
            </Button>
            <Button
              onClick={() => {
                confirmation[0] && confirmation[0]();
                setConfirmation([null, ""]);
              }}
              color="primary"
              autoFocus
            >
              Yes
            </Button>
          </DialogActions>
        </Dialog>
      </Toolbar>
    </AppBar>
  );
};

export default Header;
